import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Collapse,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSaveCid } from './hooks/useSaveCid';

export default function CidFormModal({ open, onClose, onSave, cid, initialDescription }) {
  const navigate = useNavigate();
  const { save: saveCid } = useSaveCid();
  const [formCode, setFormCode] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setFormCode(cid?.code ?? '');
      setFormDescription(cid?.description ?? initialDescription ?? '');
      setError('');
    }
  }, [open, cid, initialDescription]);

  const MAX_CODE_LENGTH = 10;
  const INVALID_CODE_MESSAGE =
    'O código da CID é composto por até 6 caracteres, entre letras e números.';

  const isValid = formCode.trim() !== '' && formDescription.trim() !== '';

  const save = () => {
    if (!isValid) return;

    if (formCode.trim().length > MAX_CODE_LENGTH) {
      setError(INVALID_CODE_MESSAGE);
      return;
    }

    const payload = {
      code: formCode.trim().toUpperCase(),
      description: formDescription.trim(),
    };
    saveCid(cid ? cid.id : null, payload)
      .then((returnFromApi) => {
        onSave(returnFromApi.data ?? payload);
        onClose();
      })
      .catch((err) => {
        if (err.response?.status === 401) {
          navigate('/expired-session');
        } else {
          setError(err.response?.data ?? 'Erro ao salvar CID.');
        }
      });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 'bold' }}>{cid ? 'Editar CID' : 'Novo CID'}</DialogTitle>
      <DialogContent>
        <Collapse in={!!error} sx={{ mb: 1 }}>
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        </Collapse>
        <TextField
          autoFocus
          label="Código"
          fullWidth
          value={formCode}
          onChange={(e) => setFormCode(e.target.value)}
          placeholder="Ex: K02.1"
          sx={{ mt: 1 }}
        />
        <TextField
          label="Descrição"
          fullWidth
          multiline
          rows={3}
          value={formDescription}
          onChange={(e) => setFormDescription(e.target.value)}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={save} disabled={!isValid}>
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

CidFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  cid: PropTypes.shape({
    id: PropTypes.number,
    code: PropTypes.string,
    description: PropTypes.string,
  }),
  initialDescription: PropTypes.string,
};

CidFormModal.defaultProps = {
  cid: null,
  initialDescription: '',
};
